import { useEffect, useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import LoadingPlaceholder, { LoadingTableRows } from "../../components/ui/LoadingPlaceholder";
import StatusPill from "../../components/ui/StatusPill";
import {
  deleteAdminClient,
  deleteAdminInvoice,
  deleteAdminJob,
  deleteAdminLead,
  deleteAdminOffer,
  deleteAdminPriceItem,
  deleteAdminRealization,
  deleteAdminSiteService,
  deleteAdminUser,
  fetchAdminClients,
  fetchAdminInvoices,
  fetchAdminJobs,
  fetchAdminLeads,
  fetchAdminOffers,
  fetchAdminOverview,
  fetchAdminPriceItems,
  fetchAdminRealizations,
  fetchAdminSiteContent,
  fetchAdminUsers,
  fetchCalendarEvents,
  saveAdminClient,
  saveAdminInvoice,
  saveAdminJob,
  saveAdminLead,
  saveAdminOffer,
  saveAdminPriceItem,
  saveAdminRealization,
  saveAdminUser,
  saveCurrentAdminProfile,
  saveAdminSiteContent,
  saveAdminSiteService,
  startGoogleCalendarConnect,
} from "../../services/adminApi";
import { setToken } from "../../services/tokenStorage";

const tabs = ["Prehled", "Poptavky", "Obsah webu", "Zakazky", "Cenik", "Nabidky", "Faktury", "Klienti", "Uzivatele", "Realizace", "Kalendar", "Ucet"];

const emptyClient = { id: "", companyName: "", billingEmail: "", billingAddress: "", siteAddress: "", notes: "" };
const emptyJob = { id: "", clientId: "", title: "", status: "planning", progressPercent: 10, siteAddress: "", plannedStart: "", plannedEnd: "", finalPrice: "", description: "" };
const emptyPrice = { id: "", title: "", unit: "ks", priceFrom: "", priceTo: "", note: "", isVisible: true, sortOrder: 0 };
const emptyOffer = { id: "", clientId: "", jobId: "", offerNumber: "", title: "", validUntil: "", subtotal: "", vatRate: 21, total: "", status: "draft" };
const emptyInvoice = { id: "", clientId: "", jobId: "", offerId: "", invoiceNumber: "", variableSymbol: "", issueDate: "", dueDate: "", subtotal: "", vatRate: 21, total: "", advanceTotal: 0, paidTotal: 0, status: "draft" };
const emptyRealization = { id: "", title: "", place: "", image: "", description: "", isVisible: true, sortOrder: 0 };
const emptyService = { id: "", title: "", description: "", price: "", isVisible: true, sortOrder: 0 };
const emptyUser = { id: "", fullName: "", email: "", phone: "", avatarUrl: "", role: "admin", password: "", isActive: true };
const defaultSiteContent = {
  heroEyebrow: "ELTT elektroinstalace",
  heroTitle: "Spolehliva elektro prace bez zbytecneho chaosu.",
  heroText: "Elektroinstalace, rekonstrukce, revize a servis pro domy, byty a mensi firmy. Jasna domluva, ciste predani a dokumenty pohromade.",
  heroImageUrl: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=1200&q=85",
  heroImageAlt: "Elektroinstalace v rozvadeci",
  servicesKicker: "Co delame",
  servicesTitle: "Hlavni sluzby",
  servicesText: "Prehled nejcastejsich elektro praci pro rodinne domy, byty a mensi provozy.",
  processKicker: "Postup",
  processTitle: "Jednoduchy proces",
  processText: "Nejdriv kratka domluva, potom prohlidka nebo naceneni, realizace a predani dokumentu.",
};

const jobStatuses = ["planning", "active", "waiting_client", "revision", "done", "cancelled"];
const moneyStatuses = ["draft", "issued", "partially_paid", "paid", "overdue", "cancelled"];
const leadStatuses = ["new", "contacted", "quoted", "won", "lost"];
const jobStatusLabels = {
  planning: "V plánu",
  active: "Probíhá",
  waiting_client: "Čeká se na klienta",
  revision: "Revize / kontrola",
  done: "Hotovo",
  cancelled: "Zrušeno",
};
const moneyStatusLabels = {
  draft: "Rozpracováno",
  issued: "Vystaveno",
  partially_paid: "Částečně uhrazeno",
  paid: "Uhrazeno",
  overdue: "Po splatnosti",
  cancelled: "Zrušeno",
};
const leadStatusLabels = {
  new: "Nová",
  contacted: "Kontaktován",
  quoted: "Nabídnuto",
  won: "Vyhráno",
  lost: "Ztraceno",
};
const statusProgress = {
  planning: 10,
  active: 35,
  waiting_client: 55,
  revision: 85,
  done: 100,
  cancelled: 0,
};

function AdminDashboard({ activeTab, onTabChange, onLogout, user, onUserUpdated }) {
  const [overview, setOverview] = useState([]);
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [offers, setOffers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [priceItems, setPriceItems] = useState([]);
  const [realizations, setRealizations] = useState([]);
  const [siteServices, setSiteServices] = useState([]);
  const [siteContentForm, setSiteContentForm] = useState(defaultSiteContent);

  const [clientForm, setClientForm] = useState(emptyClient);
  const [jobForm, setJobForm] = useState(emptyJob);
  const [priceForm, setPriceForm] = useState(emptyPrice);
  const [offerForm, setOfferForm] = useState(emptyOffer);
  const [invoiceForm, setInvoiceForm] = useState(emptyInvoice);
  const [realizationForm, setRealizationForm] = useState(emptyRealization);
  const [serviceForm, setServiceForm] = useState(emptyService);
  const [userForm, setUserForm] = useState(emptyUser);
  const [accountForm, setAccountForm] = useState({ ...emptyUser, password: "" });

  const [status, setStatus] = useState({ type: "loading", message: "Načítám administraci..." });

  const clientOptions = useMemo(() => clients.map((client) => ({
    value: client.id,
    label: client.companyName || client.billingEmail || client.id,
  })), [clients]);

  const jobOptions = useMemo(() => jobs.map((job) => ({
    value: job.id,
    label: job.title,
  })), [jobs]);

  const offerOptions = useMemo(() => offers.map((offer) => ({
    value: offer.id,
    label: offer.offerNumber || offer.title,
  })), [offers]);

  const priceOptions = useMemo(() => priceItems.map((item) => ({
    value: item.id,
    label: `${item.title} (${item.priceLabel || formatMoney(item.priceFrom || 0)})`,
  })), [priceItems]);

  const findClient = (id) => clients.find((client) => String(client.id) === String(id));
  const overdueInvoicesCount = useMemo(() => invoices.filter(isInvoiceOverdue).length, [invoices]);
  const overviewItems = useMemo(() => overview.map((item) => (
    item.label === "Faktury po splatnosti" ? { ...item, value: overdueInvoicesCount } : item
  )), [overview, overdueInvoicesCount]);

  async function loadAdminData() {
    setStatus({ type: "loading", message: "Načítám administraci..." });
    try {
      const [overviewData, usersData, leadsData, jobsData, clientsData, offersData, invoicesData, calendarData, pricingData, siteData, realizationsData] = await Promise.all([
        fetchAdminOverview(),
        fetchAdminUsers(),
        fetchAdminLeads(),
        fetchAdminJobs(),
        fetchAdminClients(),
        fetchAdminOffers(),
        fetchAdminInvoices(),
        fetchCalendarEvents(),
        fetchAdminPriceItems(),
        fetchAdminSiteContent(),
        fetchAdminRealizations(),
      ]);

      setOverview(overviewData?.stats || []);
      setUsers(usersData?.users || []);
      setLeads(leadsData?.leads || []);
      setJobs(jobsData?.jobs || []);
      setClients(clientsData?.clients || []);
      setOffers(offersData?.offers || []);
      setInvoices(invoicesData?.invoices || []);
      setCalendarEvents(calendarData?.events || []);
      setPriceItems(pricingData?.items || []);
      setSiteContentForm({ ...defaultSiteContent, ...(siteData?.content || {}) });
      setSiteServices(siteData?.services || []);
      setRealizations(realizationsData?.items || []);
      setStatus({ type: "idle", message: "" });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    }
  }

  useEffect(() => {
    loadAdminData();
  }, []);

  useEffect(() => {
    setAccountForm({
      ...emptyUser,
      id: user?.id || "",
      fullName: user?.fullName || "",
      email: user?.email || "",
      phone: user?.phone || "",
      avatarUrl: user?.avatarUrl || "",
      role: user?.role || "admin",
      password: "",
      isActive: true,
    });
  }, [user]);

  const updateForm = (setter) => (event) => {
    const { name, type, checked, value } = event.target;
    setter((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  };

  const updateJobForm = (event) => {
    const { name, value } = event.target;
    setJobForm((current) => {
      const next = { ...current, [name]: value };
      if (name === "clientId") {
        const client = findClient(value);
        if (client && !current.siteAddress) {
          next.siteAddress = client.siteAddress || client.billingAddress || "";
        }
      }
      if (name === "status") {
        next.progressPercent = statusProgress[value] ?? current.progressPercent;
      }
      return next;
    });
  };

  const replaceItem = (setter, key) => (saved) => {
    setter((current) => {
      const exists = current.some((item) => item.id === saved.id);
      return exists ? current.map((item) => (item.id === saved.id ? saved : item)) : [saved, ...current];
    });
    if (key === "client") loadAdminData();
  };

  const removeItem = (setter, id) => setter((current) => current.filter((item) => item.id !== id));

  const submitClient = async (event) => {
    event.preventDefault();
    setStatus({ type: "loading", message: "Ukládám klienta..." });
    try {
      const response = await saveAdminClient(clientForm);
      replaceItem(setClients, "client")(response.client);
      setClientForm(emptyClient);
      setStatus({ type: "idle", message: "" });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    }
  };

  const submitUser = async (event) => {
    event.preventDefault();
    setStatus({ type: "loading", message: "Ukládám uživatele..." });
    try {
      const response = await saveAdminUser(userForm);
      replaceItem(setUsers)(response.user);
      setUserForm(emptyUser);
      setStatus({ type: "idle", message: "" });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    }
  };

  const submitAccount = async (event) => {
    event.preventDefault();
    setStatus({ type: "loading", message: "Ukladam ucet..." });
    try {
      const response = await saveCurrentAdminProfile(accountForm);
      if (response?.token) {
        setToken(response.token);
      }
      onUserUpdated?.(response.user || null);
      setAccountForm((current) => ({ ...current, password: "" }));
      setStatus({ type: "idle", message: "" });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    }
  };

  const submitJob = async (event) => {
    event.preventDefault();
    setStatus({ type: "loading", message: "Ukládám zakázku..." });
    try {
      const response = await saveAdminJob(jobForm);
      replaceItem(setJobs)(response.job);
      setJobForm(emptyJob);
      setStatus({ type: "idle", message: "" });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    }
  };

  const submitPrice = async (event) => {
    event.preventDefault();
    setStatus({ type: "loading", message: "Ukládám položku ceníku..." });
    try {
      const response = await saveAdminPriceItem(priceForm);
      replaceItem(setPriceItems)(response.item);
      setPriceItems((current) => [...current].sort((a, b) => a.sortOrder - b.sortOrder));
      setPriceForm(emptyPrice);
      setStatus({ type: "idle", message: "" });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    }
  };

  const submitSiteContent = async (event) => {
    event.preventDefault();
    setStatus({ type: "loading", message: "Ukládám obsah webu..." });
    try {
      const response = await saveAdminSiteContent(siteContentForm);
      setSiteContentForm({ ...defaultSiteContent, ...(response.content || {}) });
      setStatus({ type: "idle", message: "" });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    }
  };

  const submitSiteService = async (event) => {
    event.preventDefault();
    setStatus({ type: "loading", message: "Ukládám službu..." });
    try {
      const response = await saveAdminSiteService(serviceForm);
      replaceItem(setSiteServices)(response.service);
      setSiteServices((current) => [...current].sort((a, b) => a.sortOrder - b.sortOrder));
      setServiceForm(emptyService);
      setStatus({ type: "idle", message: "" });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    }
  };

  const submitRealization = async (event) => {
    event.preventDefault();
    setStatus({ type: "loading", message: "Ukládám realizaci..." });
    try {
      const response = await saveAdminRealization(realizationForm);
      replaceItem(setRealizations)(response.item);
      setRealizations((current) => [...current].sort((a, b) => a.sortOrder - b.sortOrder));
      setRealizationForm(emptyRealization);
      setStatus({ type: "idle", message: "" });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    }
  };

  const submitOffer = async (event) => {
    event.preventDefault();
    setStatus({ type: "loading", message: "Ukládám nabídku..." });
    try {
      const response = await saveAdminOffer(offerForm);
      replaceItem(setOffers)(response.offer);
      setOfferForm(emptyOffer);
      setStatus({ type: "idle", message: "" });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    }
  };

  const submitInvoice = async (event) => {
    event.preventDefault();
    setStatus({ type: "loading", message: "Ukládám fakturu..." });
    try {
      const response = await saveAdminInvoice(invoiceForm);
      replaceItem(setInvoices)(response.invoice);
      setInvoiceForm(emptyInvoice);
      setStatus({ type: "idle", message: "" });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    }
  };

  const deleteWith = async (handler, setter, id, message, confirmation) => {
    const confirmed = await confirmDeletion(confirmation);
    if (!confirmed) return;

    setStatus({ type: "loading", message });
    try {
      await handler(id);
      removeItem(setter, id);
      setStatus({ type: "idle", message: "" });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    }
  };

  const updateLeadStatus = async (lead, nextStatus) => {
    setStatus({ type: "loading", message: "Ukládám stav poptávky..." });
    try {
      const response = await saveAdminLead({ ...lead, status: nextStatus });
      replaceItem(setLeads)(response.lead);
      setStatus({ type: "idle", message: "" });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    }
  };

  const connectGoogleCalendar = async () => {
    setStatus({ type: "loading", message: "Připravuji Google Calendar OAuth..." });
    try {
      const response = await startGoogleCalendarConnect();
      if (!response?.authorizationUrl) throw new Error("API nevrátilo Google OAuth URL.");
      window.location.href = response.authorizationUrl;
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    }
  };

  const renderOverview = () => (
    <div className="grid gap-6 xl:grid-cols-[1fr_360px]">
      <div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {status.type === "loading" && !overviewItems.length
            ? Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="card p-5">
                <LoadingPlaceholder className="h-4 w-24" />
                <LoadingPlaceholder className="mt-4 h-8 w-16" />
              </div>
            ))
            : overviewItems.map((item) => {
              const isOverdueStat = item.label === "Faktury po splatnosti" && toNumber(item.value) > 0;
              return (
              <div key={item.label} className={`card p-5 ${isOverdueStat ? "border-red-400/60 bg-red-500/10" : ""}`}>
                <div className={`text-sm font-medium ${isOverdueStat ? "text-red-200" : "text-slate-400"}`}>{item.label}</div>
                <div className={`mt-2 text-3xl font-extrabold ${isOverdueStat ? "text-red-100" : "text-white"}`}>{item.value}</div>
              </div>
            );
            })}
        </div>
        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <MiniList title="Nove poptavky" items={leads.slice(0, 5)} primary="fullName" secondary="jobType" onItemClick={showLeadDetail} />
          <MiniList title="Rozpracovane zakazky" items={jobs.slice(0, 5)} primary="title" secondary="clientName" />
          <MiniList title="Posledni faktury" items={invoices.slice(0, 5)} primary="invoiceNumber" secondary="totalLabel" />
        </div>
      </div>
      {renderCalendar()}
    </div>
  );

  const renderJobs = () => (
    <CrudPanel
      title="Zakazky"
      formTitle={jobForm.id ? "Upravit zakázku" : "Nová zakázka"}
      form={(
        <form className="card p-5" onSubmit={submitJob}>
          <FormGrid>
            <Select label="Klient" name="clientId" value={jobForm.clientId} onChange={updateJobForm} options={clientOptions} required helper="Po výběrz doplní adresu realizace z klienta." />
            <Input label="Název" name="title" value={jobForm.title} onChange={updateJobForm} required />
            <Select label="Stav" name="status" value={jobForm.status} onChange={updateJobForm} options={jobStatuses.map((value) => ({ value, label: jobStatusLabels[value] }))} helper="Stav automaticky nastavi orientacni prubeh." />
            <Input label="Průběh %" name="progressPercent" type="number" min="0" max="100" value={jobForm.progressPercent} onChange={updateJobForm} />
            <Input label="Plán začátek" name="plannedStart" type="date" value={jobForm.plannedStart || ""} onChange={updateJobForm} />
            <Input label="Plán konec" name="plannedEnd" type="date" value={jobForm.plannedEnd || ""} onChange={updateJobForm} />
            <Input label="Cena" name="finalPrice" type="number" value={jobForm.finalPrice ?? ""} onChange={updateJobForm} />
            <Input label="Adresa" name="siteAddress" value={jobForm.siteAddress || ""} onChange={updateJobForm} />
            <Textarea label="Popis" name="description" value={jobForm.description || ""} onChange={updateJobForm} />
            <FormButtons onReset={() => setJobForm(emptyJob)} />
          </FormGrid>
        </form>
      )}
    >
      <AdminTable headers={["Zakazka", "Klient", "Stav", "Prubeh", "Rozpocet", "Akce"]} loading={status.type === "loading" && !jobs.length} columns={6}>
        {jobs.map((job) => (
          <tr key={job.id}>
            <td className="px-5 py-4 font-medium text-white">{job.title}</td>
            <td className="px-5 py-4 text-slate-300">{job.clientName}</td>
            <td className="px-5 py-4"><StatusPill>{jobStatusLabels[job.status] || job.status}</StatusPill></td>
            <td className="px-5 py-4 text-slate-300">{job.progressPercent}%</td>
            <td className="px-5 py-4 text-white">{job.budget}</td>
            <Actions onEdit={() => setJobForm(normalizeJob(job))} onDelete={() => deleteWith(deleteAdminJob, setJobs, job.id, "Mažu zakázku...", describeJobDelete(job, offers, invoices))} />
          </tr>
        ))}
      </AdminTable>
    </CrudPanel>
  );

  const renderLeads = () => (
    <div className="card overflow-hidden">
      <div className="border-b border-cyan-300/10 p-5">
        <h2 className="text-lg font-bold text-white">Poptavky</h2>
        <p className="mt-1 text-sm text-slate-400">Zprávy z kontaktního formuláře. Kliknutím oteřeš celý detail.</p>
      </div>
      <AdminTable headers={["Kontakt", "Typ prace", "Zprava", "Stav", "Vytvoreno", "Akce"]} loading={status.type === "loading" && !leads.length} columns={6}>
        {leads.map((lead) => (
          <tr key={lead.id} className="cursor-pointer hover:bg-cyan-300/5" onClick={() => showLeadDetail(lead)}>
            <td className="px-5 py-4">
              <div className="font-semibold text-white">{lead.fullName}</div>
              <div className="text-xs text-slate-400">{lead.email || "-"} · {lead.phone || "-"}</div>
            </td>
            <td className="px-5 py-4 text-slate-300">{lead.jobType || "-"}</td>
            <td className="max-w-xs px-5 py-4 text-slate-300">
              <div className="line-clamp-2">{lead.message}</div>
            </td>
            <td className="px-5 py-4" onClick={(event) => event.stopPropagation()}>
              <select className="input min-w-36" value={lead.status} onChange={(event) => updateLeadStatus(lead, event.target.value)}>
                {leadStatuses.map((value) => <option key={value} value={value}>{leadStatusLabels[value]}</option>)}
              </select>
            </td>
            <td className="px-5 py-4 text-slate-300">{formatDateTime(lead.createdAt)}</td>
            <Actions
              onEdit={() => showLeadDetail(lead)}
              onDelete={(event) => {
                event?.stopPropagation?.();
                deleteWith(deleteAdminLead, setLeads, lead.id, "Mažu poptavku...", describeSimpleDelete("poptavku", lead.fullName));
              }}
            />
          </tr>
        ))}
      </AdminTable>
      {status.type !== "loading" && !leads.length && (
        <p className="p-5 text-sm text-slate-400">Žádné poptávky zatím nejsou.</p>
      )}
    </div>
  );

  const renderPricing = () => (
    <CrudPanel
      title="Cenik"
      formTitle={priceForm.id ? "Upravit položku" : "Nová položka"}
      form={(
        <form className="card p-5" onSubmit={submitPrice}>
          <FormGrid>
            <Input label="Nazev" name="title" value={priceForm.title} onChange={updateForm(setPriceForm)} required />
            <Input label="Cena od" name="priceFrom" type="number" value={priceForm.priceFrom ?? ""} onChange={updateForm(setPriceForm)} />
            <Input label="Cena do" name="priceTo" type="number" value={priceForm.priceTo ?? ""} onChange={updateForm(setPriceForm)} />
            <Input label="Jednotka" name="unit" value={priceForm.unit} onChange={updateForm(setPriceForm)} required />
            <Input label="Pořadí" name="sortOrder" type="number" value={priceForm.sortOrder} onChange={updateForm(setPriceForm)} />
            <Textarea label="Poznamka" name="note" value={priceForm.note || ""} onChange={updateForm(setPriceForm)} />
            <label className="flex items-center gap-3 text-sm font-medium text-slate-200">
              <input name="isVisible" type="checkbox" checked={priceForm.isVisible} onChange={updateForm(setPriceForm)} />
              Zobrazit na webu
            </label>
            <FormButtons onReset={() => setPriceForm(emptyPrice)} />
          </FormGrid>
        </form>
      )}
    >
      <AdminTable headers={["Poradi", "Polozka", "Cena", "Viditelna", "Akce"]} loading={status.type === "loading" && !priceItems.length} columns={5}>
        {priceItems.map((item) => (
          <tr key={item.id}>
            <td className="px-5 py-4 text-slate-300">{item.sortOrder}</td>
            <td className="px-5 py-4"><div className="font-semibold text-white">{item.title}</div><div className="text-xs text-slate-400">{item.note}</div></td>
            <td className="px-5 py-4 text-white">{item.priceLabel}</td>
            <td className="px-5 py-4 text-slate-300">{item.isVisible ? "Ano" : "Ne"}</td>
            <Actions onEdit={() => setPriceForm(normalizePrice(item))} onDelete={() => deleteWith(deleteAdminPriceItem, setPriceItems, item.id, "Mazu polozku...", describeSimpleDelete("polozku ceniku", item.title))} />
          </tr>
        ))}
      </AdminTable>
    </CrudPanel>
  );

  const renderSiteContent = () => (
    <div className="grid gap-6">
      <div className="card p-5">
        <h2 className="text-lg font-bold text-white">Homepage</h2>
        <form className="mt-4 grid gap-4" onSubmit={submitSiteContent}>
          <FormGrid>
            <Input label="Eyebrow" name="heroEyebrow" value={siteContentForm.heroEyebrow} onChange={updateForm(setSiteContentForm)} />
            <Textarea label="Hero nadpis" name="heroTitle" value={siteContentForm.heroTitle} onChange={updateForm(setSiteContentForm)} />
            <Textarea label="Hero text" name="heroText" value={siteContentForm.heroText} onChange={updateForm(setSiteContentForm)} />
            <Input label="URL uvodni fotky" name="heroImageUrl" value={siteContentForm.heroImageUrl} onChange={updateForm(setSiteContentForm)} />
            <Input label="Popis fotky" name="heroImageAlt" value={siteContentForm.heroImageAlt} onChange={updateForm(setSiteContentForm)} />
            <Input label="Nadpis sekce sluzeb" name="servicesTitle" value={siteContentForm.servicesTitle} onChange={updateForm(setSiteContentForm)} />
            <Textarea label="Text sekce sluzeb" name="servicesText" value={siteContentForm.servicesText} onChange={updateForm(setSiteContentForm)} />
            <Input label="Nadpis procesu" name="processTitle" value={siteContentForm.processTitle} onChange={updateForm(setSiteContentForm)} />
            <Textarea label="Text procesu" name="processText" value={siteContentForm.processText} onChange={updateForm(setSiteContentForm)} />
            <button type="submit" className="btn-primary">Ulozit obsah homepage</button>
          </FormGrid>
        </form>
      </div>

      <div className="card overflow-hidden">
        <div className="border-b border-cyan-300/10 p-5">
          <h2 className="text-lg font-bold text-white">Sluzby na webu</h2>
          <p className="mt-1 text-sm text-slate-400">Tyto polozky se zobrazuji na homepage i na strance sluzeb.</p>
        </div>
        <form className="border-b border-cyan-300/10 p-5" onSubmit={submitSiteService}>
          <h3 className="text-sm font-bold uppercase tracking-wide text-cyan-300">
            {serviceForm.id ? "Upravit sluzbu" : "Nova sluzba"}
          </h3>
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div className="grid gap-4">
              <Input label="Nazev" name="title" value={serviceForm.title} onChange={updateForm(setServiceForm)} required />
              <Textarea label="Popis" name="description" value={serviceForm.description || ""} onChange={updateForm(setServiceForm)} />
            </div>
            <div className="grid content-start gap-4">
              <Input label="Cena / popisek" name="price" value={serviceForm.price || ""} onChange={updateForm(setServiceForm)} />
              <Input label="Poradi" name="sortOrder" type="number" value={serviceForm.sortOrder} onChange={updateForm(setServiceForm)} />
              <label className="flex items-center gap-3 text-sm font-medium text-slate-200">
                <input name="isVisible" type="checkbox" checked={serviceForm.isVisible} onChange={updateForm(setServiceForm)} />
                Zobrazit na webu
              </label>
              <FormButtons onReset={() => setServiceForm(emptyService)} />
            </div>
          </div>
        </form>
        <AdminTable headers={["Sluzba", "Cena", "Viditelna", "Akce"]} loading={status.type === "loading" && !siteServices.length} columns={4}>
          {siteServices.map((service) => (
            <tr key={service.id}>
              <td className="px-5 py-4"><div className="font-semibold text-white">{service.title}</div><div className="text-xs text-slate-400">{service.description}</div></td>
              <td className="px-5 py-4 text-white">{service.price || "-"}</td>
              <td className="px-5 py-4 text-slate-300">{service.isVisible ? "Ano" : "Ne"}</td>
              <Actions onEdit={() => setServiceForm(normalizeService(service))} onDelete={() => deleteWith(deleteAdminSiteService, setSiteServices, service.id, "Mazu sluzbu...", describeSimpleDelete("sluzbu", service.title))} />
            </tr>
          ))}
        </AdminTable>
      </div>
    </div>
  );

  const renderOffers = () => (
    <MoneyPanel
      kind="offer"
      title="Nabidky"
      formTitle={offerForm.id ? "Upravit nabidku" : "Nova nabidka"}
      form={offerForm}
      setForm={setOfferForm}
      emptyForm={emptyOffer}
      onSubmit={submitOffer}
      clientOptions={clientOptions}
      jobOptions={jobOptions}
      items={offers}
      loading={status.type === "loading" && !offers.length}
      onEdit={(item) => setOfferForm(normalizeOffer(item))}
      onDelete={(item) => deleteWith(deleteAdminOffer, setOffers, item.id, "Mazu nabidku...", describeOfferDelete(item, invoices))}
      clients={clients}
      jobs={jobs}
      offers={offers}
      priceItems={priceItems}
      priceOptions={priceOptions}
    />
  );

  const renderInvoices = () => (
    <MoneyPanel
      kind="invoice"
      title="Faktury"
      formTitle={invoiceForm.id ? "Upravit fakturu" : "Nova faktura"}
      form={invoiceForm}
      setForm={setInvoiceForm}
      emptyForm={emptyInvoice}
      onSubmit={submitInvoice}
      clientOptions={clientOptions}
      jobOptions={jobOptions}
      offerOptions={offerOptions}
      items={invoices}
      loading={status.type === "loading" && !invoices.length}
      onEdit={(item) => setInvoiceForm(normalizeInvoice(item))}
      onDelete={(item) => deleteWith(deleteAdminInvoice, setInvoices, item.id, "Mazu fakturu...", describeInvoiceDelete(item))}
      clients={clients}
      jobs={jobs}
      offers={offers}
      priceItems={priceItems}
      priceOptions={priceOptions}
    />
  );

  const renderRealizations = () => (
    <CrudPanel
      title="Realizace"
      formTitle={realizationForm.id ? "Upravit realizaci" : "Nova realizace"}
      form={(
        <form className="card p-5" onSubmit={submitRealization}>
          <FormGrid>
            <Input label="Nazev" name="title" value={realizationForm.title} onChange={updateForm(setRealizationForm)} required />
            <Input label="Misto" name="place" value={realizationForm.place} onChange={updateForm(setRealizationForm)} required />
            <Input label="URL fotky" name="image" value={realizationForm.image} onChange={updateForm(setRealizationForm)} required />
            <Textarea label="Popis" name="description" value={realizationForm.description || ""} onChange={updateForm(setRealizationForm)} />
            <Input label="Poradi" name="sortOrder" type="number" value={realizationForm.sortOrder} onChange={updateForm(setRealizationForm)} />
            <label className="flex items-center gap-3 text-sm font-medium text-slate-200">
              <input name="isVisible" type="checkbox" checked={realizationForm.isVisible} onChange={updateForm(setRealizationForm)} />
              Zobrazit na webu
            </label>
            <FormButtons onReset={() => setRealizationForm(emptyRealization)} />
          </FormGrid>
        </form>
      )}
    >
      <AdminTable headers={["Realizace", "Misto", "Viditelna", "Akce"]} loading={status.type === "loading" && !realizations.length} columns={4}>
        {realizations.map((item) => (
          <tr key={item.id}>
            <td className="px-5 py-4">
              <div className="flex items-center gap-3">
                <img src={item.image} alt={item.title} className="h-12 w-16 rounded-md object-cover" />
                <div><div className="font-semibold text-white">{item.title}</div><div className="text-xs text-slate-400">{item.description}</div></div>
              </div>
            </td>
            <td className="px-5 py-4 text-slate-300">{item.place}</td>
            <td className="px-5 py-4 text-slate-300">{item.isVisible ? "Ano" : "Ne"}</td>
            <Actions onEdit={() => setRealizationForm(normalizeRealization(item))} onDelete={() => deleteWith(deleteAdminRealization, setRealizations, item.id, "Mazu realizaci...", describeSimpleDelete("realizaci", item.title))} />
          </tr>
        ))}
      </AdminTable>
    </CrudPanel>
  );

  const renderClients = () => (
    <CrudPanel
      title="Klienti"
      formTitle={clientForm.id ? "Upravit klienta" : "Novy klient"}
      form={(
        <form className="card p-5" onSubmit={submitClient}>
          <FormGrid>
            <Input label="Nazev / jmeno" name="companyName" value={clientForm.companyName} onChange={updateForm(setClientForm)} required />
            <Input label="Fakturacni e-mail" name="billingEmail" type="email" value={clientForm.billingEmail || ""} onChange={updateForm(setClientForm)} />
            <Input label="Fakturacni adresa" name="billingAddress" value={clientForm.billingAddress || ""} onChange={updateForm(setClientForm)} />
            <Input label="Adresa realizace" name="siteAddress" value={clientForm.siteAddress || ""} onChange={updateForm(setClientForm)} />
            <Textarea label="Poznamky" name="notes" value={clientForm.notes || ""} onChange={updateForm(setClientForm)} />
            <FormButtons onReset={() => setClientForm(emptyClient)} />
          </FormGrid>
        </form>
      )}
    >
      <AdminTable headers={["Klient", "E-mail", "Telefon", "Adresa", "Akce"]} loading={status.type === "loading" && !clients.length} columns={5}>
        {clients.map((client) => (
          <tr key={client.id}>
            <td className="px-5 py-4 font-medium text-white">{client.companyName}</td>
            <td className="px-5 py-4 text-slate-300">{client.billingEmail || "-"}</td>
            <td className="px-5 py-4 text-slate-300">{client.phone || "-"}</td>
            <td className="px-5 py-4 text-slate-300">{client.siteAddress || client.billingAddress || "-"}</td>
            <Actions onEdit={() => setClientForm(normalizeClient(client))} onDelete={() => deleteWith(deleteAdminClient, setClients, client.id, "Mazu klienta...", describeClientDelete(client, jobs, offers, invoices))} />
          </tr>
        ))}
      </AdminTable>
    </CrudPanel>
  );

  const renderUsers = () => (
    <CrudPanel
      title="Uzivatele"
      formTitle={userForm.id ? "Upravit uzivatele" : "Novy uzivatel"}
      form={(
        <form className="card p-5" onSubmit={submitUser}>
          <FormGrid>
            <Input label="Jmeno" name="fullName" value={userForm.fullName} onChange={updateForm(setUserForm)} required />
            <Input label="E-mail" name="email" type="email" value={userForm.email} onChange={updateForm(setUserForm)} required />
            <Input label="Telefon" name="phone" value={userForm.phone || ""} onChange={updateForm(setUserForm)} />
            <Input label="Avatar URL" name="avatarUrl" value={userForm.avatarUrl || ""} onChange={updateForm(setUserForm)} />
            <Select label="Role" name="role" value={userForm.role} onChange={updateForm(setUserForm)} options={[
              { value: "admin", label: "Admin" },
              { value: "technician", label: "Technik" },
              { value: "client", label: "Klient" },
            ]} />
            <Input label={userForm.id ? "Nove heslo" : "Heslo"} name="password" type="password" value={userForm.password || ""} onChange={updateForm(setUserForm)} required={!userForm.id} helper={userForm.id ? "Vyplnte jen pokud chcete heslo zmenit." : "Minimalne 6 znaku."} />
            <label className="flex items-center gap-3 text-sm font-medium text-slate-200">
              <input name="isActive" type="checkbox" checked={userForm.isActive} onChange={updateForm(setUserForm)} />
              Aktivni ucet
            </label>
            <FormButtons onReset={() => setUserForm(emptyUser)} />
          </FormGrid>
        </form>
      )}
    >
      <AdminTable headers={["Uzivatel", "Role", "Telefon", "Stav", "Akce"]} loading={status.type === "loading" && !users.length} columns={5}>
        {users.map((item) => (
          <tr key={item.id}>
            <td className="px-5 py-4">
              <div className="font-semibold text-white">{item.fullName}</div>
              <div className="text-xs text-slate-400">{item.email}</div>
            </td>
            <td className="px-5 py-4 text-slate-300">{userRoleLabel(item.role)}</td>
            <td className="px-5 py-4 text-slate-300">{item.phone || "-"}</td>
            <td className="px-5 py-4"><StatusPill>{item.isActive ? "Aktivni" : "Vypnuty"}</StatusPill></td>
            <Actions
              onEdit={() => setUserForm(normalizeUser(item))}
              onDelete={() => deleteWith(deleteAdminUser, setUsers, item.id, "Mazu uzivatele...", describeSimpleDelete("uzivatele", item.fullName))}
            />
          </tr>
        ))}
      </AdminTable>
    </CrudPanel>
  );

  const renderAccount = () => (
    <div className="mx-auto max-w-2xl">
      <h2 className="mb-3 text-lg font-bold text-white">Muj ucet</h2>
      <form className="card p-5" onSubmit={submitAccount}>
        <FormGrid>
          <Input label="Jmeno" name="fullName" value={accountForm.fullName} onChange={updateForm(setAccountForm)} required />
          <Input label="E-mail" name="email" type="email" value={accountForm.email} onChange={updateForm(setAccountForm)} required />
          <Input label="Telefon" name="phone" value={accountForm.phone || ""} onChange={updateForm(setAccountForm)} />
          <Input label="Avatar URL" name="avatarUrl" value={accountForm.avatarUrl || ""} onChange={updateForm(setAccountForm)} />
          <Input label="Nove heslo" name="password" type="password" value={accountForm.password || ""} onChange={updateForm(setAccountForm)} helper="Vyplnte jen pokud chcete zmenit heslo. Minimalne 6 znaku." />
          <button type="submit" className="btn-primary">Ulozit ucet</button>
        </FormGrid>
      </form>
    </div>
  );

  const renderCalendar = () => (
    <aside className="card p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-white">Google Calendar</h2>
          <p className="mt-1 text-sm text-slate-400">Nadchazejici udalosti prehledne v textu.</p>
        </div>
        <button type="button" className="btn-secondary px-3 py-2" onClick={connectGoogleCalendar}>Pripojit</button>
      </div>
      <div className="mt-5 space-y-3">
        {status.type === "loading" && !calendarEvents.length
          ? Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="rounded-md border border-cyan-300/15 bg-[#07131d] p-3">
              <LoadingPlaceholder className="h-3 w-20" />
              <LoadingPlaceholder className="mt-3 h-4 w-3/4" />
              <LoadingPlaceholder className="mt-2 h-4 w-1/2" />
            </div>
          ))
          : calendarEvents.map((event) => (
            <div key={event.id || event.title} className="rounded-md border border-cyan-300/15 bg-[#07131d] p-3">
              <div className="text-xs font-semibold text-cyan-300">{event.startsAt || event.time}</div>
              <div className="mt-1 text-sm font-semibold text-white">{event.title}</div>
              <div className="text-sm text-slate-400">{event.location || event.place || "-"}</div>
            </div>
          ))}
        {!calendarEvents.length && status.type !== "loading" && <p className="text-sm text-slate-400">Zadne udalosti.</p>}
      </div>
    </aside>
  );

  const renderActiveTab = () => {
    if (activeTab === "Prehled") return renderOverview();
    if (activeTab === "Poptavky") return renderLeads();
    if (activeTab === "Obsah webu") return renderSiteContent();
    if (activeTab === "Zakazky") return renderJobs();
    if (activeTab === "Cenik") return renderPricing();
    if (activeTab === "Nabidky") return renderOffers();
    if (activeTab === "Faktury") return renderInvoices();
    if (activeTab === "Klienti") return renderClients();
    if (activeTab === "Uzivatele") return renderUsers();
    if (activeTab === "Realizace") return renderRealizations();
    if (activeTab === "Kalendar") return renderCalendar();
    if (activeTab === "Ucet") return renderAccount();
    return null;
  };

  return (
    <section className="page-section">
      <div className="shell">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <p className="eyebrow">Administrace</p>
            <h1 className="mt-3 text-4xl font-extrabold tracking-tight text-white">Sprava ELTT</h1>
            <p className="mt-3 text-sm leading-6 text-slate-300">
              Prihlasen: {user?.fullName || user?.email || "admin"}
            </p>
          </div>
          <button type="button" onClick={onLogout} className="btn-secondary">Odhlasit</button>
        </div>

        {status.message && (
          <div className={`mt-6 rounded-md border p-4 text-sm ${
            status.type === "error" ? "border-red-400/30 bg-red-400/10 text-red-200" : "border-cyan-300/20 bg-cyan-300/10 text-cyan-100"
          }`}>
            {status.message}
          </div>
        )}

        <div className="mt-8 overflow-x-auto">
          <div className="flex min-w-max gap-2">
            {tabs.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => onTabChange(tab)}
                className={`rounded-md px-3 py-2 text-sm font-semibold ${
                  activeTab === tab ? "bg-cyan-300 text-slate-950" : "bg-cyan-300/5 text-slate-300 hover:bg-cyan-300/10"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6">{renderActiveTab()}</div>
      </div>
    </section>
  );
}

function CrudPanel({ title, formTitle, form, children }) {
  return (
    <div className="grid gap-6 xl:grid-cols-[1fr_380px]">
      <div className="card overflow-hidden">
        <div className="border-b border-cyan-300/10 p-5">
          <h2 className="text-lg font-bold text-white">{title}</h2>
        </div>
        {children}
      </div>
      <div>
        <h2 className="mb-3 text-lg font-bold text-white">{formTitle}</h2>
        {form}
      </div>
    </div>
  );
}

function MoneyPanel({
  kind,
  title,
  formTitle,
  form,
  setForm,
  emptyForm,
  onSubmit,
  clientOptions,
  jobOptions,
  offerOptions = [],
  items,
  loading,
  onEdit,
  onDelete,
  clients = [],
  jobs = [],
  offers = [],
  priceItems = [],
  priceOptions = [],
}) {
  const [calculator, setCalculator] = useState({ priceItemId: "", quantity: 1, unitPrice: "" });
  const selectedClient = clients.find((client) => String(client.id) === String(form.clientId));
  const selectedJob = jobs.find((job) => String(job.id) === String(form.jobId));
  const selectedOffer = offers.find((offer) => String(offer.id) === String(form.offerId));
  const scopedJobs = useMemo(() => {
    if (!form.clientId) return jobs;
    return jobs.filter((job) => String(job.clientId) === String(form.clientId));
  }, [form.clientId, jobs]);
  const scopedOffers = useMemo(() => {
    return offers.filter((offer) => {
      if (form.jobId && String(offer.jobId) !== String(form.jobId)) return false;
      if (form.clientId && String(offer.clientId) !== String(form.clientId)) return false;
      return true;
    });
  }, [form.clientId, form.jobId, offers]);
  const scopedJobOptions = useMemo(() => scopedJobs.map((job) => ({ value: job.id, label: job.title })), [scopedJobs]);
  const scopedOfferOptions = useMemo(() => scopedOffers.map((offer) => ({
    value: offer.id,
    label: offer.offerNumber || offer.title,
  })), [scopedOffers]);
  const subtotal = toNumber(form.subtotal);
  const vatRate = toNumber(form.vatRate);
  const total = totalWithVat(subtotal, vatRate);
  const advanceTotal = toNumber(form.advanceTotal);
  const paidTotal = toNumber(form.paidTotal);
  const coveredTotal = advanceTotal + paidTotal;
  const remaining = Math.max(0, total - coveredTotal);
  const overdueCount = useMemo(() => (
    kind === "invoice" ? items.filter(isInvoiceOverdue).length : 0
  ), [items, kind]);
  const displayItems = useMemo(() => {
    if (kind !== "invoice") return items;
    return [...items].sort((a, b) => {
      const aOverdue = isInvoiceOverdue(a) ? 0 : 1;
      const bOverdue = isInvoiceOverdue(b) ? 0 : 1;
      if (aOverdue !== bOverdue) return aOverdue - bOverdue;
      return String(a.dueDate || "").localeCompare(String(b.dueDate || ""));
    });
  }, [items, kind]);

  const update = (event) => {
    const { name, value } = event.target;
    setForm((current) => {
      const next = { ...current, [name]: value };
      const client = name === "clientId" ? clients.find((item) => String(item.id) === String(value)) : null;
      const job = name === "jobId" ? jobs.find((item) => String(item.id) === String(value)) : null;
      const offer = name === "offerId" ? offers.find((item) => String(item.id) === String(value)) : null;

      if (name === "jobId" && job) {
        next.clientId = job.clientId || next.clientId;
        next.title = kind === "offer" && !next.title ? job.title : next.title;
        next.subtotal = current.subtotal || job.finalPrice || "";
        if (kind === "invoice") {
          const relatedOffer = offers.find((item) => String(item.jobId) === String(job.id));
          if (relatedOffer) {
            next.offerId = relatedOffer.id;
            next.subtotal = relatedOffer.subtotal ?? next.subtotal;
            next.vatRate = relatedOffer.vatRate ?? next.vatRate;
            next.total = relatedOffer.total ?? next.total;
          }
        }
      }

      if (name === "clientId" && client) {
        if (kind === "offer" && !next.title) {
          next.title = `Nabidka pro ${client.companyName}`;
        }

        const clientJobs = jobs.filter((item) => String(item.clientId) === String(client.id));
        if (!current.jobId && clientJobs.length === 1) {
          const onlyJob = clientJobs[0];
          next.jobId = onlyJob.id;
          next.subtotal = current.subtotal || onlyJob.finalPrice || "";
          next.title = kind === "offer" && !next.title ? onlyJob.title : next.title;
        }
      }

      if (name === "offerId" && offer) {
        next.clientId = offer.clientId || next.clientId;
        next.jobId = offer.jobId || next.jobId;
        next.subtotal = offer.subtotal ?? next.subtotal;
        next.vatRate = offer.vatRate ?? next.vatRate;
        next.total = offer.total ?? next.total;
      }

      if (name === "issueDate") {
        next.dueDate = current.dueDate || addDays(value, 14);
      }

      const shouldRecalculate = ["subtotal", "vatRate", "jobId", "offerId", "clientId"].includes(name);
      if (shouldRecalculate) {
        next.total = totalWithVat(next.subtotal, next.vatRate);
      }

      if (kind === "invoice") {
        next.variableSymbol = buildVariableSymbol(next.invoiceNumber, next.variableSymbol, current.invoiceNumber);
        next.status = resolveInvoiceStatus(next.status, next.total, next.paidTotal, next.dueDate, next.advanceTotal);
      }

      return next;
    });
  };

  const applyCalculatorItem = (event) => {
    const priceItemId = event.target.value;
    const priceItem = priceItems.find((item) => String(item.id) === String(priceItemId));
    setCalculator((current) => ({
      ...current,
      priceItemId,
      unitPrice: priceItem ? String(priceItem.priceFrom || priceItem.priceTo || 0) : "",
    }));
  };

  const updateCalculator = (event) => {
    const { name, value } = event.target;
    setCalculator((current) => ({ ...current, [name]: value }));
  };

  const addCalculatedLine = () => {
    const lineTotal = roundMoney(toNumber(calculator.quantity) * toNumber(calculator.unitPrice));
    if (!lineTotal) return;
    setForm((current) => {
      const nextSubtotal = roundMoney(toNumber(current.subtotal) + lineTotal);
      return {
        ...current,
        subtotal: nextSubtotal,
        total: totalWithVat(nextSubtotal, current.vatRate),
      };
    });
  };

  const fillDates = () => {
    setForm((current) => {
      if (kind === "offer") {
        return { ...current, validUntil: current.validUntil || addDays(todayIso(), 14) };
      }
      const issueDate = current.issueDate || todayIso();
      return { ...current, issueDate, dueDate: current.dueDate || addDays(issueDate, 14) };
    });
  };

  const syncTotal = () => {
    setForm((current) => ({
      ...current,
      total: totalWithVat(current.subtotal, current.vatRate),
      status: kind === "invoice" ? resolveInvoiceStatus(current.status, totalWithVat(current.subtotal, current.vatRate), current.paidTotal, current.dueDate, current.advanceTotal) : current.status,
    }));
  };

  return (
    <CrudPanel
      title={title}
      formTitle={formTitle}
      form={(
        <form className="card p-5" onSubmit={onSubmit}>
          <FormGrid>
            <Select label="Klient" name="clientId" value={form.clientId} onChange={update} options={clientOptions} required helper="Po vyberu se propoji zakazka, pokud je jednoznacna." />
            <Select label="Zakazka" name="jobId" value={form.jobId || ""} onChange={update} options={scopedJobOptions.length ? scopedJobOptions : jobOptions} helper="Doplni klienta, cenu a u faktury i souvisejici nabidku." />
            {kind === "invoice" && <Select label="Nabidka" name="offerId" value={form.offerId || ""} onChange={update} options={scopedOfferOptions.length ? scopedOfferOptions : offerOptions} helper="Prevezme klienta, zakazku, zaklad, DPH a celkem." />}
            <Input label={kind === "invoice" ? "Cislo faktury" : "Cislo nabidky"} name={kind === "invoice" ? "invoiceNumber" : "offerNumber"} value={kind === "invoice" ? form.invoiceNumber : form.offerNumber} onChange={update} />
            {kind === "offer" && <Input label="Nazev" name="title" value={form.title} onChange={update} required />}
            {kind === "invoice" && <Input label="Variabilni symbol" name="variableSymbol" value={form.variableSymbol || ""} onChange={update} />}
            {kind === "offer" && <Input label="Platnost do" name="validUntil" type="date" value={form.validUntil || ""} onChange={update} />}
            {kind === "invoice" && <Input label="Vystaveno" name="issueDate" type="date" value={form.issueDate || ""} onChange={update} />}
            {kind === "invoice" && <Input label="Splatnost" name="dueDate" type="date" value={form.dueDate || ""} onChange={update} />}
            <Input label="Zaklad" name="subtotal" type="number" value={form.subtotal ?? ""} onChange={update} />
            <Input label="DPH %" name="vatRate" type="number" value={form.vatRate ?? 0} onChange={update} />
            <Input label="Celkem" name="total" type="number" value={form.total ?? ""} onChange={update} readOnly helper="Dopocitava se ze zakladu a DPH." />
            {kind === "invoice" && <Input label="Zaloha" name="advanceTotal" type="number" value={form.advanceTotal ?? 0} onChange={update} helper="Castka prijata predem, automaticky snizi doplatek." />}
            {kind === "invoice" && <Input label="Uhrazeno" name="paidTotal" type="number" value={form.paidTotal ?? 0} onChange={update} />}
            <Select label="Stav" name="status" value={form.status} onChange={update} options={moneyStatuses.map((value) => ({ value, label: moneyStatusLabels[value] }))} />
            <SmartSummary
              kind={kind}
              client={selectedClient}
              job={selectedJob}
              offer={selectedOffer}
              subtotal={subtotal}
              vatRate={vatRate}
              total={total}
              advanceTotal={advanceTotal}
              paidTotal={paidTotal}
              remaining={remaining}
              onFillDates={fillDates}
              onSyncTotal={syncTotal}
            />
            <PriceCalculator
              calculator={calculator}
              options={priceOptions}
              onPickItem={applyCalculatorItem}
              onChange={updateCalculator}
              onAdd={addCalculatedLine}
            />
            <FormButtons onReset={() => setForm(emptyForm)} />
          </FormGrid>
        </form>
      )}
    >
      {kind === "invoice" && overdueCount > 0 && (
        <div className="border-b border-red-400/30 bg-red-500/15 px-5 py-3 text-sm font-semibold text-red-100">
          Po splatnosti: {overdueCount} faktur. Jsou serazene nahore a oznacene cervene.
        </div>
      )}
      <AdminTable headers={kind === "invoice" ? ["Cislo", "Klient", "Splatnost", "Celkem", "Stav", "Akce"] : ["Cislo", "Nazev", "Klient", "Celkem", "Stav", "Akce"]} loading={loading} columns={6}>
        {displayItems.map((item) => {
          const overdue = kind === "invoice" && isInvoiceOverdue(item);
          return (
          <tr key={item.id} className={overdue ? "border-l-4 border-red-400 bg-red-500/20 shadow-[inset_0_0_0_1px_rgba(248,113,113,0.25)]" : ""}>
            <td className="px-5 py-4 font-medium text-white">{kind === "invoice" ? item.invoiceNumber : item.offerNumber}</td>
            {kind === "invoice"
              ? <td className="px-5 py-4 text-slate-300">{item.clientName}</td>
              : <td className="px-5 py-4 text-slate-300">{item.title}</td>}
            {kind === "invoice"
              ? <td className={`px-5 py-4 ${overdue ? "font-bold text-red-200" : "text-slate-300"}`}>{item.dueDate}{overdue ? " - po splatnosti" : ""}</td>
              : <td className="px-5 py-4 text-slate-300">{item.clientName}</td>}
            <td className="px-5 py-4 text-white">
              <div>{item.totalLabel}</div>
              {kind === "invoice" && toNumber(item.advanceTotal) > 0 && (
                <div className="text-xs font-semibold text-cyan-200">Zaloha {formatMoney(item.advanceTotal)}</div>
              )}
            </td>
            <td className="px-5 py-4"><StatusPill tone={overdue ? "danger" : "default"}>{overdue ? "Po splatnosti" : (moneyStatusLabels[item.status] || item.status)}</StatusPill></td>
            <Actions
              onEdit={() => onEdit(item)}
              onDelete={() => onDelete(item)}
              extra={kind === "invoice" ? (
                <button type="button" className="btn-secondary px-3 py-2" onClick={() => downloadInvoicePdf(item, clients, jobs, offers)}>PDF</button>
              ) : null}
            />
          </tr>
        );
        })}
      </AdminTable>
    </CrudPanel>
  );
}

function SmartSummary({ kind, client, job, offer, subtotal, vatRate, total, advanceTotal, paidTotal, remaining, onFillDates, onSyncTotal }) {
  return (
    <div className="rounded-md border border-cyan-300/15 bg-cyan-300/5 p-3 text-sm">
      <div className="grid gap-2 text-slate-300">
        <SummaryRow label="Klient" value={client?.companyName || "Vyberte klienta"} />
        <SummaryRow label="Zakazka" value={job?.title || "Nepovinne, ale propoji data"} />
        {kind === "invoice" && <SummaryRow label="Z nabidky" value={offer?.offerNumber || "Lze prevzit castky z nabidky"} />}
        <SummaryRow label="Zaklad" value={formatMoney(subtotal)} />
        <SummaryRow label="DPH" value={`${vatRate || 0} %`} />
        <SummaryRow label="Celkem" value={formatMoney(total)} strong />
        {kind === "invoice" && advanceTotal > 0 && <SummaryRow label="Zaloha" value={formatMoney(advanceTotal)} />}
        {kind === "invoice" && paidTotal > 0 && <SummaryRow label="Uhrazeno" value={formatMoney(paidTotal)} />}
        {kind === "invoice" && <SummaryRow label="Doplatit" value={formatMoney(remaining)} strong={remaining > 0} />}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button type="button" className="btn-secondary min-h-9 px-3 py-1.5" onClick={onFillDates}>Doplnit data</button>
        <button type="button" className="btn-secondary min-h-9 px-3 py-1.5" onClick={onSyncTotal}>Prepocitat</button>
      </div>
      {kind === "invoice" && (paidTotal > 0 || advanceTotal > 0) && (
        <p className="mt-2 text-xs text-slate-400">Zaloha i uhrada se zapocitaji do stavu faktury automaticky.</p>
      )}
    </div>
  );
}

function SummaryRow({ label, value, strong = false }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-slate-400">{label}</span>
      <span className={`text-right ${strong ? "font-bold text-white" : "text-slate-200"}`}>{value}</span>
    </div>
  );
}

function PriceCalculator({ calculator, options, onPickItem, onChange, onAdd }) {
  const lineTotal = roundMoney(toNumber(calculator.quantity) * toNumber(calculator.unitPrice));

  return (
    <div className="rounded-md border border-cyan-300/15 bg-[#07131d] p-3">
      <div className="text-sm font-bold text-white">Rychly vypocet z ceniku</div>
      <div className="mt-3 grid gap-3">
        <Select label="Polozka" name="priceItemId" value={calculator.priceItemId} onChange={onPickItem} options={options} />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Mnozstvi" name="quantity" type="number" min="0" step="0.01" value={calculator.quantity} onChange={onChange} />
          <Input label="Cena / jednotka" name="unitPrice" type="number" min="0" step="0.01" value={calculator.unitPrice} onChange={onChange} />
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-slate-400">Radek celkem: {formatMoney(lineTotal)}</span>
          <button type="button" className="btn-secondary min-h-9 px-3 py-1.5" onClick={onAdd}>Pridat</button>
        </div>
      </div>
    </div>
  );
}

function AdminTable({ headers, loading, columns, children }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] text-left text-sm">
        <thead className="bg-cyan-300/5 text-xs uppercase tracking-wide text-slate-400">
          <tr>{headers.map((header) => <th key={header} className="px-5 py-3">{header}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-cyan-300/10">
          {loading ? <LoadingTableRows rows={5} columns={columns} /> : children}
        </tbody>
      </table>
    </div>
  );
}

function MiniList({ title, items, primary, secondary, onItemClick }) {
  return (
    <div className="card p-5">
      <h2 className="text-lg font-bold text-white">{title}</h2>
      <div className="mt-4 space-y-3">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            className="block w-full rounded-md border border-cyan-300/10 bg-[#07131d] p-3 text-left transition hover:border-cyan-300/35 hover:bg-cyan-300/5"
            onClick={() => onItemClick?.(item)}
          >
            <div className="font-semibold text-white">{item[primary]}</div>
            <div className="text-sm text-slate-400">{item[secondary]}</div>
          </button>
        ))}
        {!items.length && <p className="text-sm text-slate-400">Zadna data.</p>}
      </div>
    </div>
  );
}

function Actions({ onEdit, onDelete, extra = null }) {
  return (
    <td className="px-5 py-4">
      <div className="flex justify-end gap-2">
        {extra}
        <button type="button" className="btn-secondary px-3 py-2" onClick={(event) => { event.stopPropagation(); onEdit(event); }}>Upravit</button>
        <button type="button" className="btn-secondary px-3 py-2" onClick={(event) => { event.stopPropagation(); onDelete(event); }}>Smazat</button>
      </div>
    </td>
  );
}

function FormGrid({ children }) {
  return <div className="grid gap-4">{children}</div>;
}

function Input({ label, helper, ...props }) {
  return (
    <label className="grid gap-1.5 text-sm font-medium text-slate-200">
      {label}
      <input className="input" {...props} />
      {helper && <span className="text-xs font-normal text-slate-400">{helper}</span>}
    </label>
  );
}

function Textarea({ label, helper, ...props }) {
  return (
    <label className="grid gap-1.5 text-sm font-medium text-slate-200">
      {label}
      <textarea className="textarea" {...props} />
      {helper && <span className="text-xs font-normal text-slate-400">{helper}</span>}
    </label>
  );
}

function Select({ label, options, helper, ...props }) {
  return (
    <label className="grid gap-1.5 text-sm font-medium text-slate-200">
      {label}
      <select className="input" {...props}>
        <option value="">Vyberte</option>
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
      {helper && <span className="text-xs font-normal text-slate-400">{helper}</span>}
    </label>
  );
}

function FormButtons({ onReset }) {
  return (
    <div className="flex gap-2">
      <button type="submit" className="btn-primary flex-1">Ulozit</button>
      <button type="button" className="btn-secondary" onClick={onReset}>Vycistit</button>
    </div>
  );
}

async function confirmDeletion({ title, html, confirmText = "Ano, smazat" }) {
  const result = await Swal.fire({
    title,
    html,
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: "Zrusit",
    reverseButtons: true,
    focusCancel: true,
    background: "#0a1722",
    color: "#e5f7fb",
    iconColor: "#facc15",
    confirmButtonColor: "#ef4444",
    cancelButtonColor: "#164e63",
    customClass: {
      popup: "admin-delete-dialog",
      confirmButton: "admin-delete-confirm",
      cancelButton: "admin-delete-cancel",
    },
  });

  return result.isConfirmed;
}

function describeClientDelete(client, jobs, offers, invoices) {
  const clientJobs = jobs.filter((job) => String(job.clientId) === String(client.id));
  const clientOffers = offers.filter((offer) => String(offer.clientId) === String(client.id));
  const clientInvoices = invoices.filter((invoice) => String(invoice.clientId) === String(client.id));
  return {
    title: `Smazat klienta ${client.companyName}?`,
    html: deleteHtml([
      "Smazanim klienta se smaze i vse, co je na nej navazane.",
      `${clientJobs.length} zakazek`,
      `${clientOffers.length} nabidek`,
      `${clientInvoices.length} faktur vcetne plateb`,
    ]),
  };
}

function describeJobDelete(job, offers, invoices) {
  const jobOffers = offers.filter((offer) => String(offer.jobId) === String(job.id));
  const offerIds = new Set(jobOffers.map((offer) => String(offer.id)));
  const jobInvoices = invoices.filter((invoice) => String(invoice.jobId) === String(job.id) || offerIds.has(String(invoice.offerId)));
  return {
    title: `Smazat zakazku ${job.title}?`,
    html: deleteHtml([
      "Smazanim zakazky se smaze i jeji obchodni historie.",
      `${jobOffers.length} navazanych nabidek`,
      `${jobInvoices.length} navazanych faktur vcetne plateb`,
    ]),
  };
}

function describeOfferDelete(offer, invoices) {
  const offerInvoices = invoices.filter((invoice) => String(invoice.offerId) === String(offer.id));
  return {
    title: `Smazat nabidku ${offer.offerNumber || offer.title}?`,
    html: deleteHtml([
      "Smazanim nabidky se smaze i fakturace vytvorena z teto nabidky.",
      `${offerInvoices.length} navazanych faktur vcetne plateb`,
    ]),
  };
}

function describeInvoiceDelete(invoice) {
  return {
    title: `Smazat fakturu ${invoice.invoiceNumber}?`,
    html: deleteHtml([
      "Smaze se pouze tato faktura a jeji platby.",
      "Klient, zakazka ani nabidka zustanou zachovane.",
    ]),
  };
}

function describeSimpleDelete(kind, name) {
  return {
    title: `Smazat ${kind}?`,
    html: deleteHtml([name || "Tato polozka bude trvale smazana."]),
  };
}

function showLeadDetail(lead) {
  Swal.fire({
    title: lead.fullName || "Poptavka",
    html: `
      <div style="text-align:left">
        <p style="margin:0 0 8px;color:#cbd5e1"><strong style="color:#67e8f9">Stav:</strong> ${escapeHtml(leadStatusLabels[lead.status] || lead.status || "-")}</p>
        <p style="margin:0 0 8px;color:#cbd5e1"><strong style="color:#67e8f9">Typ prace:</strong> ${escapeHtml(lead.jobType || "-")}</p>
        <p style="margin:0 0 8px;color:#cbd5e1"><strong style="color:#67e8f9">E-mail:</strong> ${escapeHtml(lead.email || "-")}</p>
        <p style="margin:0 0 8px;color:#cbd5e1"><strong style="color:#67e8f9">Telefon:</strong> ${escapeHtml(lead.phone || "-")}</p>
        <p style="margin:0 0 8px;color:#cbd5e1"><strong style="color:#67e8f9">Vytvoreno:</strong> ${escapeHtml(formatDateTime(lead.createdAt))}</p>
        <div style="margin-top:14px;border-top:1px solid rgba(103,232,249,.22);padding-top:14px;color:#e5f7fb;white-space:pre-wrap">${escapeHtml(lead.message || "-")}</div>
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: lead.email ? "Napsat e-mail" : "Zavrit",
    cancelButtonText: "Zavrit",
    background: "#0a1722",
    color: "#e5f7fb",
    confirmButtonColor: "#22d3ee",
    cancelButtonColor: "#164e63",
  }).then((result) => {
    if (result.isConfirmed && lead.email) {
      window.location.href = `mailto:${lead.email}`;
    }
  });
}

function deleteHtml(lines) {
  return `
    <div style="text-align:left">
      ${lines.map((line) => `<p style="margin:0 0 8px;color:#cbd5e1">${escapeHtml(line)}</p>`).join("")}
      <p style="margin:12px 0 0;color:#67e8f9;font-weight:700">Tuto akci nelze vratit zpet.</p>
    </div>
  `;
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("cs-CZ", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function downloadInvoicePdf(invoice, clients, jobs, offers) {
  const client = clients.find((item) => String(item.id) === String(invoice.clientId));
  const job = jobs.find((item) => String(item.id) === String(invoice.jobId));
  const offer = offers.find((item) => String(item.id) === String(invoice.offerId));
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const total = toNumber(invoice.total);
  const subtotal = toNumber(invoice.subtotal);
  const vat = Math.max(0, total - subtotal);
  const advanceTotal = toNumber(invoice.advanceTotal);
  const paidTotal = toNumber(invoice.paidTotal);
  const remaining = Math.max(0, total - advanceTotal - paidTotal);

  doc.setFillColor(2, 7, 13);
  doc.rect(0, 0, pageWidth, 42, "F");
  doc.setTextColor(103, 232, 249);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("ELTT elektroinstalace", 16, 16);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(28);
  doc.text("Faktura", 16, 31);

  doc.setFontSize(12);
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.text(invoice.invoiceNumber || "Bez cisla", 150, 18);
  doc.setFont("helvetica", "normal");
  doc.text(`Vystaveno: ${invoice.issueDate || "-"}`, 150, 26);
  doc.text(`Splatnost: ${invoice.dueDate || "-"}`, 150, 34);

  doc.setDrawColor(34, 211, 238);
  doc.setLineWidth(0.4);
  doc.line(16, 52, 194, 52);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Odberatel", 16, 64);
  doc.text("Souvislosti", 112, 64);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(client?.companyName || invoice.clientName || "-", 16, 72);
  doc.text(client?.billingEmail || "-", 16, 79);
  doc.text(client?.billingAddress || client?.siteAddress || "-", 16, 86, { maxWidth: 76 });
  doc.text(`Zakazka: ${job?.title || "-"}`, 112, 72, { maxWidth: 78 });
  doc.text(`Nabidka: ${offer?.offerNumber || "-"}`, 112, 79, { maxWidth: 78 });
  doc.text(`Variabilni symbol: ${invoice.variableSymbol || "-"}`, 112, 86);

  doc.setFillColor(241, 245, 249);
  doc.roundedRect(16, 108, 178, 46, 2, 2, "F");
  doc.setFont("helvetica", "bold");
  doc.setTextColor(15, 23, 42);
  doc.text("Polozka", 22, 121);
  doc.text("Castka", 170, 121, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.text(job?.title || offer?.title || "Elektro prace dle dohody", 22, 135, { maxWidth: 110 });
  doc.text(formatMoney(subtotal), 170, 135, { align: "right" });

  doc.setFont("helvetica", "bold");
  doc.text("Zaklad", 128, 172);
  doc.text(formatMoney(subtotal), 190, 172, { align: "right" });
  doc.text(`DPH ${invoice.vatRate || 0} %`, 128, 181);
  doc.text(formatMoney(vat), 190, 181, { align: "right" });
  if (advanceTotal > 0) {
    doc.text("Zaloha", 128, 190);
    doc.text(`-${formatMoney(advanceTotal)}`, 190, 190, { align: "right" });
  }
  if (paidTotal > 0) {
    doc.text("Uhrazeno", 128, advanceTotal > 0 ? 199 : 190);
    doc.text(`-${formatMoney(paidTotal)}`, 190, advanceTotal > 0 ? 199 : 190, { align: "right" });
  }
  doc.setFontSize(15);
  doc.text("Doplatit", 128, advanceTotal > 0 || paidTotal > 0 ? 214 : 195);
  doc.text(formatMoney(remaining), 190, advanceTotal > 0 || paidTotal > 0 ? 214 : 195, { align: "right" });

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(71, 85, 105);
  doc.text("Doklad byl vygenerovan z administrace ELTT.", 16, 280);
  doc.save(`${invoice.invoiceNumber || "faktura"}.pdf`);
}

function normalizeClient(client) {
  return { ...emptyClient, ...client };
}

function normalizeUser(user) {
  return { ...emptyUser, ...user, password: "" };
}

function normalizeJob(job) {
  return { ...emptyJob, ...job, finalPrice: job.finalPrice ?? "", plannedStart: job.plannedStart || "", plannedEnd: job.plannedEnd || "" };
}

function normalizePrice(item) {
  return { ...emptyPrice, ...item, priceFrom: item.priceFrom ?? "", priceTo: item.priceTo ?? "" };
}

function normalizeOffer(item) {
  return { ...emptyOffer, ...item, subtotal: item.subtotal ?? "", vatRate: item.vatRate ?? 0, total: item.total ?? "", validUntil: item.validUntil || "" };
}

function normalizeInvoice(item) {
  return { ...emptyInvoice, ...item, subtotal: item.subtotal ?? "", vatRate: item.vatRate ?? 0, total: item.total ?? "", advanceTotal: item.advanceTotal ?? 0, paidTotal: item.paidTotal ?? 0, issueDate: item.issueDate || "", dueDate: item.dueDate || "" };
}

function normalizeRealization(item) {
  return { ...emptyRealization, ...item, image: item.image || "" };
}

function normalizeService(item) {
  return { ...emptyService, ...item, price: item.price || "" };
}

function userRoleLabel(role) {
  if (role === "admin") return "Admin";
  if (role === "technician") return "Technik";
  if (role === "client") return "Klient";
  return role || "-";
}

function toNumber(value) {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
}

function roundMoney(value) {
  return Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;
}

function totalWithVat(subtotal, vatRate) {
  return roundMoney(toNumber(subtotal) * (1 + toNumber(vatRate) / 100));
}

function formatMoney(value) {
  return `${new Intl.NumberFormat("cs-CZ", { maximumFractionDigits: 0 }).format(roundMoney(value))} Kc`;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function addDays(dateValue, days) {
  const base = dateValue ? new Date(`${dateValue}T00:00:00`) : new Date();
  if (Number.isNaN(base.getTime())) return "";
  base.setDate(base.getDate() + days);
  return base.toISOString().slice(0, 10);
}

function buildVariableSymbol(invoiceNumber, currentValue, previousInvoiceNumber = "") {
  const previousAutoValue = String(previousInvoiceNumber || "").replace(/\D/g, "").slice(-10);
  if (currentValue && currentValue !== previousAutoValue) return currentValue;
  return String(invoiceNumber || "").replace(/\D/g, "").slice(-10);
}

function resolveInvoiceStatus(currentStatus, total, paidTotal, dueDate, advanceTotal = 0) {
  if (currentStatus === "cancelled" || currentStatus === "draft") return currentStatus;
  const totalValue = toNumber(total);
  const paidValue = toNumber(paidTotal) + toNumber(advanceTotal);
  if (totalValue > 0 && paidValue >= totalValue) return "paid";
  if (paidValue > 0) return "partially_paid";
  if (dueDate && new Date(`${dueDate}T23:59:59`) < new Date()) return "overdue";
  return currentStatus || "issued";
}

function isInvoiceOverdue(invoice) {
  if (invoice?.isOverdue) return true;
  if (!invoice?.dueDate || invoice.status === "cancelled" || invoice.status === "paid") return false;
  const dueDate = new Date(`${invoice.dueDate}T23:59:59`);
  if (Number.isNaN(dueDate.getTime()) || dueDate >= new Date()) return false;
  return toNumber(invoice.paidTotal) + toNumber(invoice.advanceTotal) < toNumber(invoice.total);
}

export default AdminDashboard;
